// Project memory (F07, RF-MEM-01..07): search with provenance and the "historical" label
// (superseded/obsolete versions are never shown as current, AC-24), sources (ingest a
// decision / PRD version / repository paths, reject, tombstone), index versions, datasets and
// evaluations, and statistics. Memory is project-only (D11.7 A): nothing crosses projects.
import { useState, type FormEvent } from "react";
import { useI18n } from "../../i18n/index.tsx";
import type { MessageKey } from "../../i18n/en.ts";
import { listPrds } from "../../api/endpoints.ts";
import { getMemorySource, getMemoryStats, ingestMemory, listMemorySources, rejectMemorySource, searchMemory, tombstoneMemorySource } from "../../api/endpoints-wave3.ts";
import {
  MEMORY_SOURCE_KINDS,
  MEMORY_SOURCE_STATES,
  MEMORY_STRATEGIES,
  type IngestInput,
  type IngestResponse,
  type MemorySearchResponse,
  type MemorySource,
  type MemorySourceDetail,
  type MemorySourceState,
  type MemoryStrategy,
  type TombstoneResult,
} from "../../api/types-wave3.ts";
import { useAsync } from "../../lib/useAsync.ts";
import { formatMs, formatNumber } from "../../lib/format.ts";
import { Tabs, TabPanel } from "../../components/Tabs.tsx";
import { Button } from "../../components/Button.tsx";
import { Dialog } from "../../components/Dialog.tsx";
import { CheckboxField, SelectField, TextAreaField, TextField } from "../../components/Field.tsx";
import { Banner, EmptyState, ErrorBanner, LoadingBlock } from "../../components/Feedback.tsx";
import { ActionError, fieldErrors } from "../../components/ActionError.tsx";
import { useToast } from "../../components/Toast.tsx";
import { BookIcon, HistoryIcon, SearchIcon } from "../../components/icons.tsx";
import { Hash, LoadMore, ReasonDialog, StatePill, splitList, useCursorList, useProjectResources, useProjectRole } from "../wave3/common.tsx";
import { MemoryIndexPanel } from "./MemoryIndex.tsx";

const SUB_TABS = ["search", "sources", "index", "stats"] as const;
type SubTab = (typeof SUB_TABS)[number];

export const SOURCE_STATE_VIEW: Record<MemorySourceState, { tone: "success" | "warning" | "neutral" | "danger"; label: MessageKey }> = {
  candidate: { tone: "warning", label: "memState.candidate" },
  validated: { tone: "success", label: "memState.validated" },
  obsolete: { tone: "neutral", label: "memState.obsolete" },
  rejected: { tone: "danger", label: "memState.rejected" },
};

export function CurrencyLabel({ label }: { label: "current" | "historical" }) {
  const { t } = useI18n();
  return label === "historical" ? (
    <span className="badge badge--warning" data-testid="historical-label">
      <HistoryIcon size={12} /> {t("memory.historical")}
    </span>
  ) : (
    <span className="badge" data-testid="current-label">
      {t("memory.current")}
    </span>
  );
}

export function ProjectMemory({ projectId }: { projectId: string }) {
  const { t } = useI18n();
  const [tab, setTab] = useState<SubTab>("search");
  const prefix = `memory-${projectId}`;
  return (
    <div className="stack" data-testid="project-memory">
      <p className="field-hint">{t("memory.projectOnly")}</p>
      <Tabs
        idPrefix={prefix}
        label={t("memory.sections")}
        value={tab}
        onChange={setTab}
        items={[
          { id: "search", label: t("memory.tab.search") },
          { id: "sources", label: t("memory.tab.sources") },
          { id: "index", label: t("memory.tab.index") },
          { id: "stats", label: t("memory.tab.stats") },
        ]}
      />
      <TabPanel idPrefix={prefix} id={tab}>
        {tab === "search" ? <MemorySearch projectId={projectId} /> : null}
        {tab === "sources" ? <MemorySources projectId={projectId} /> : null}
        {tab === "index" ? <MemoryIndexPanel projectId={projectId} /> : null}
        {tab === "stats" ? <MemoryStatsPanel projectId={projectId} /> : null}
      </TabPanel>
    </div>
  );
}

// ---------------- Search ----------------

function MemorySearch({ projectId }: { projectId: string }) {
  const { t } = useI18n();
  const [query, setQuery] = useState("");
  const [k, setK] = useState("10");
  const [historical, setHistorical] = useState(false);
  const [strategy, setStrategy] = useState<MemoryStrategy | "">("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<MemorySearchResponse | null>(null);
  const [local, setLocal] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!query.trim()) {
      setLocal(t("memory.queryRequired"));
      return;
    }
    setLocal(null);
    setBusy(true);
    setError(null);
    try {
      setResult(await searchMemory(projectId, { query: query.trim(), k: Math.min(50, Math.max(1, Number(k) || 10)), include_historical: historical, ...(strategy ? { strategy } : {}) }));
    } catch (err) {
      setError(err);
      setResult(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" aria-labelledby={`memory-search-${projectId}`} data-testid="memory-search">
      <div className="card-header">
        <SearchIcon size={18} />
        <h2 id={`memory-search-${projectId}`} className="card-title">
          {t("memory.searchTitle")}
        </h2>
      </div>
      <div className="card-body stack">
        <form className="stack" onSubmit={submit} noValidate role="search" aria-label={t("memory.searchTitle")}>
          <TextField label={t("memory.query")} value={query} onChange={(e) => setQuery(e.target.value)} error={local} maxLength={4000} />
          <div className="field-row field-row--compact">
            <TextField label={t("memory.k")} type="number" min={1} max={50} value={k} onChange={(e) => setK(e.target.value)} inputMode="numeric" fieldClassName="field--narrow" />
            <SelectField label={t("memory.strategy")} value={strategy} onChange={(e) => setStrategy(e.target.value as MemoryStrategy | "")} hint={t("memory.strategyHint")}>
              <option value="">{t("memory.strategyDefault")}</option>
              {MEMORY_STRATEGIES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </SelectField>
            <label className="check">
              <input type="checkbox" checked={historical} onChange={(e) => setHistorical(e.target.checked)} />
              {t("memory.includeHistorical")}
            </label>
            <Button type="submit" variant="primary" loading={busy} icon={<SearchIcon size={16} />}>
              {t("memory.search")}
            </Button>
          </div>
        </form>
        <ActionError error={error} onDismiss={() => setError(null)} />
        {result ? <SearchResults result={result} /> : null}
      </div>
    </section>
  );
}

export function SearchResults({ result }: { result: MemorySearchResponse }) {
  const { t } = useI18n();
  return (
    <div className="stack" data-testid="memory-results" aria-live="polite">
      <p className="subtle">
        {t("memory.resultMeta", { n: result.hits.length, strategy: result.strategy, latency: formatMs(result.latency_ms) })} · {t("memory.indexVersion")} <Hash value={result.index_version_id} />
        {result.filters.eligible_chunks !== null ? ` · ${t("memory.eligibleChunks", { n: result.filters.eligible_chunks })}` : ""}
      </p>
      {result.degraded.length > 0 ? (
        <Banner tone="warning" testId="memory-degraded">
          <p>{t("memory.degradedNotice")}</p>
        </Banner>
      ) : null}
      <Banner tone="info">
        <p>{result.notice}</p>
      </Banner>
      {result.hits.length === 0 ? (
        <EmptyState icon={<SearchIcon />} title={t("memory.noHits")} headingLevel={3}>
          <p>{t("memory.noHitsBody")}</p>
        </EmptyState>
      ) : (
        <ol className="hit-list">
          {result.hits.map((h) => (
            <li key={h.chunk_id} className="hit" data-testid="memory-hit" data-historical={String(h.historical)}>
              <div className="hit-head">
                <span className="badge mono">#{h.rank}</span>
                <span className="mono">{h.source_key}</span>
                <span className="subtle">v{h.source_version}</span>
                <CurrencyLabel label={h.label} />
                <StatePill tone={SOURCE_STATE_VIEW[h.provenance.state].tone} label={t(SOURCE_STATE_VIEW[h.provenance.state].label)} />
                <span className="spacer" />
                <span className="subtle mono">{t("memory.score", { score: h.score.toFixed(4) })}</span>
              </div>
              <p className="hit-excerpt user-content">{h.excerpt}</p>
              <details>
                <summary>{t("memory.provenance")}</summary>
                <dl className="kv kv--compact">
                  <div>
                    <dt>{t("memory.sourceRef")}</dt>
                    <dd className="mono">{h.provenance.source_ref}</dd>
                  </div>
                  <div>
                    <dt>{t("memory.commit")}</dt>
                    <dd>
                      <Hash value={h.provenance.origin_commit} />
                    </dd>
                  </div>
                  <div>
                    <dt>{t("memory.author")}</dt>
                    <dd className="mono">{h.provenance.author || "—"}</dd>
                  </div>
                  <div>
                    <dt>{t("memory.classification")}</dt>
                    <dd>{h.provenance.classification}</dd>
                  </div>
                  <div>
                    <dt>{t("memory.lines")}</dt>
                    <dd>{h.provenance.start_line !== null ? `${h.provenance.start_line}–${h.provenance.end_line ?? "?"}` : "—"}</dd>
                  </div>
                  <div>
                    <dt>{t("memory.scores")}</dt>
                    <dd className="mono">
                      {Object.entries(h.scores)
                        .map(([k, v]) => `${k}=${Number(v).toFixed(4)}`)
                        .join(" · ") || "—"}
                    </dd>
                  </div>
                  {h.symbols.length > 0 ? (
                    <div>
                      <dt>{t("memory.symbols")}</dt>
                      <dd className="mono">{h.symbols.join(", ")}</dd>
                    </div>
                  ) : null}
                  {h.superseded_by ? (
                    <div>
                      <dt>{t("memory.supersededBy")}</dt>
                      <dd>
                        <Hash value={h.superseded_by} />
                      </dd>
                    </div>
                  ) : null}
                </dl>
              </details>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ---------------- Sources ----------------

function MemorySources({ projectId }: { projectId: string }) {
  const { t, formatDateTime } = useI18n();
  const role = useProjectRole(projectId);
  const maintainer = role.atLeast("maintainer");
  const [state, setState] = useState("");
  const [kind, setKind] = useState("");
  const [current, setCurrent] = useState<"true" | "false" | "all">("true");
  const [detailId, setDetailId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<MemorySource | null>(null);
  const [tombstoning, setTombstoning] = useState<MemorySource | null>(null);
  const [tombResult, setTombResult] = useState<TombstoneResult | null>(null);
  const list = useCursorList<MemorySource>((cursor, signal) => listMemorySources(projectId, { state: state || undefined, kind: kind || undefined, current, cursor, limit: 50 }, signal), [projectId, state, kind, current]);

  return (
    <div className="section-stack">
      <section className="card" aria-labelledby={`memory-sources-${projectId}`} data-testid="memory-sources">
        <div className="card-header">
          <BookIcon size={18} />
          <h2 id={`memory-sources-${projectId}`} className="card-title">
            {t("memory.sourcesTitle")}
          </h2>
        </div>
        <div className="card-body stack">
          <div className="field-row field-row--compact">
            <SelectField label={t("memory.filter.state")} value={state} onChange={(e) => setState(e.target.value)}>
              <option value="">{t("common.all")}</option>
              {MEMORY_SOURCE_STATES.map((s) => (
                <option key={s} value={s}>
                  {t(SOURCE_STATE_VIEW[s].label)}
                </option>
              ))}
            </SelectField>
            <SelectField label={t("memory.filter.kind")} value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">{t("common.all")}</option>
              {MEMORY_SOURCE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`memKind.${k}`)}
                </option>
              ))}
            </SelectField>
            <SelectField label={t("memory.filter.versions")} value={current} onChange={(e) => setCurrent(e.target.value as "true" | "false" | "all")}>
              <option value="true">{t("memory.filter.currentOnly")}</option>
              <option value="false">{t("memory.filter.historicalOnly")}</option>
              <option value="all">{t("memory.filter.allVersions")}</option>
            </SelectField>
          </div>
          {tombResult ? (
            <Banner tone="info" title={t("memory.tombstoned", { key: tombResult.source_key })}>
              <p data-testid="tombstone-result">
                {t("memory.tombstoneCounts", { versions: tombResult.versions, chunks: tombResult.chunks_removed, embeddings: tombResult.embeddings_removed, retrievals: tombResult.retrieval_items_removed })}
              </p>
            </Banner>
          ) : null}
          {list.error && !list.items ? (
            <ErrorBanner error={list.error} onRetry={list.reload} title={t("memory.sourcesFailed")} />
          ) : list.items === null ? (
            <LoadingBlock label={t("common.loading")} />
          ) : list.items.length === 0 ? (
            <EmptyState icon={<BookIcon />} title={t("memory.noSourcesTitle")} headingLevel={3}>
              <p>{maintainer ? t("memory.noSourcesBodyMaintainer") : t("memory.noSourcesBody")}</p>
            </EmptyState>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <caption className="sr-only">{t("memory.sourcesTitle")}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t("memory.col.source")}</th>
                    <th scope="col">{t("memory.col.state")}</th>
                    <th scope="col">{t("memory.col.origin")}</th>
                    <th scope="col">{t("memory.col.created")}</th>
                    <th scope="col">
                      <span className="sr-only">{t("common.actions")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {list.items.map((s) => {
                    const view = SOURCE_STATE_VIEW[s.state];
                    return (
                      <tr key={s.id} data-testid="memory-source-row" data-label={s.label}>
                        <td>
                          <span className="mono">{s.source_key}</span> <span className="subtle">v{s.version}</span>
                          <span className="row" style={{ marginTop: 4 }}>
                            <span className="badge">{t(`memKind.${s.kind}`)}</span>
                            <CurrencyLabel label={s.label} />
                          </span>
                        </td>
                        <td>
                          <StatePill tone={view.tone} label={t(view.label)} />
                          <span className="subtle" style={{ display: "block" }}>
                            {s.classification}
                          </span>
                        </td>
                        <td>
                          <span className="mono">{s.author}</span>
                          <span style={{ display: "block" }}>
                            <Hash value={s.origin_commit} />
                          </span>
                        </td>
                        <td>{formatDateTime(s.created_at)}</td>
                        <td>
                          <div className="row row--wrap">
                            <Button size="sm" variant="ghost" onClick={() => setDetailId(s.id)}>
                              {t("memory.versions")}
                            </Button>
                            {maintainer && s.state !== "rejected" ? (
                              <Button size="sm" onClick={() => setRejecting(s)}>
                                {t("memory.reject")}
                              </Button>
                            ) : null}
                            {maintainer ? (
                              <Button size="sm" variant="danger" onClick={() => setTombstoning(s)}>
                                {t("memory.tombstone")}
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {list.moreError ? <ErrorBanner error={list.moreError} onRetry={() => void list.loadMore()} /> : null}
          <LoadMore cursor={list.cursor} busy={list.moreBusy} count={list.items?.length ?? 0} onMore={() => void list.loadMore()} />
        </div>
      </section>
      {maintainer ? <IngestForm projectId={projectId} onDone={list.reload} /> : null}
      <SourceVersionsDialog id={detailId} onClose={() => setDetailId(null)} />
      <ReasonDialog
        open={rejecting !== null}
        title={t("memory.rejectTitle", { key: rejecting?.source_key ?? "" })}
        body={<p>{t("memory.rejectBody")}</p>}
        confirmLabel={t("memory.reject")}
        onClose={() => setRejecting(null)}
        onConfirm={async (reason) => {
          if (rejecting) await rejectMemorySource(rejecting.id, reason);
          list.reload();
        }}
      />
      <ReasonDialog
        open={tombstoning !== null}
        title={t("memory.tombstoneTitle", { key: tombstoning?.source_key ?? "" })}
        body={<p>{t("memory.tombstoneBody")}</p>}
        confirmLabel={t("memory.tombstone")}
        danger
        onClose={() => setTombstoning(null)}
        onConfirm={async (reason) => {
          if (tombstoning) setTombResult(await tombstoneMemorySource(tombstoning.id, reason));
          list.reload();
        }}
      />
    </div>
  );
}

function SourceVersionsDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { t, formatDateTime } = useI18n();
  const detail = useAsync((s) => (id ? getMemorySource(id, s) : Promise.resolve(null as MemorySourceDetail | null)), [id]);
  const d = detail.data;
  return (
    <Dialog open={id !== null} onClose={onClose} title={d ? d.source_key : t("memory.versions")} variant="side" testId="memory-source-drawer">
      {detail.loading ? (
        <LoadingBlock label={t("common.loading")} />
      ) : detail.error ? (
        <ErrorBanner error={detail.error} />
      ) : d ? (
        <div className="stack">
          <dl className="kv">
            <div>
              <dt>{t("memory.contentHash")}</dt>
              <dd>
                <Hash value={d.content_hash} />
              </dd>
            </div>
            <div>
              <dt>{t("memory.classification")}</dt>
              <dd>{d.classification}</dd>
            </div>
          </dl>
          <h3 className="subheading">{t("memory.versionHistory")}</h3>
          <ol className="plain-list">
            {d.versions.map((v) => (
              <li key={v.id} className="row row--wrap" data-testid="memory-version">
                <span className="badge mono">v{v.version}</span>
                <CurrencyLabel label={v.label} />
                <StatePill tone={SOURCE_STATE_VIEW[v.state].tone} label={t(SOURCE_STATE_VIEW[v.state].label)} />
                <span className="subtle">{formatDateTime(v.created_at)}</span>
                <Hash value={v.content_hash} />
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </Dialog>
  );
}

function IngestForm({ projectId, onDone }: { projectId: string; onDone: () => void }) {
  const { t } = useI18n();
  const { toast } = useToast();
  const [origin, setOrigin] = useState<IngestInput["origin"]>("decision");
  const [key, setKey] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [evidence, setEvidence] = useState("");
  const [validated, setValidated] = useState(false);
  const [prdVersion, setPrdVersion] = useState("");
  const [workspace, setWorkspace] = useState("");
  const [commit, setCommit] = useState("");
  const [paths, setPaths] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<IngestResponse | null>(null);
  const prds = useAsync(() => (origin === "prd_version" ? listPrds(projectId) : Promise.resolve({ items: [] })), [projectId, origin]);
  const resources = useProjectResources(projectId);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    let input: IngestInput;
    if (origin === "decision") input = { origin, key: key.trim(), title: title.trim(), body, evidence_refs: splitList(evidence), validated };
    else if (origin === "prd_version") input = { origin, prd_version_id: prdVersion };
    else input = { origin, workspace_id: workspace, commit: commit.trim(), paths: splitList(paths) };
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const r = await ingestMemory(projectId, input);
      setResult(r);
      toast(t("memory.ingested"), "success");
      onDone();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  const fe = fieldErrors(error);
  const versions = (prds.data?.items ?? []).flatMap((p) => (p.versions ?? (p.latest_version ? [p.latest_version] : [])).map((v) => ({ id: v.id, label: `${p.title} — v${v.version} (${v.extraction_state})` })));
  return (
    <form className="stack card card--pad" onSubmit={submit} noValidate data-testid="memory-ingest" aria-labelledby={`ingest-${projectId}`}>
      <h2 id={`ingest-${projectId}`} className="section-title">
        {t("memory.ingestTitle")}
      </h2>
      <p className="field-hint">{t("memory.ingestHint")}</p>
      <ActionError error={error} />
      <SelectField label={t("memory.origin")} value={origin} onChange={(e) => setOrigin(e.target.value as IngestInput["origin"])}>
        <option value="decision">{t("memory.origin.decision")}</option>
        <option value="prd_version">{t("memory.origin.prd_version")}</option>
        <option value="repository">{t("memory.origin.repository")}</option>
      </SelectField>
      {origin === "decision" ? (
        <div className="form-grid form-grid--2">
          <TextField label={t("memory.decisionKey")} hint={t("memory.decisionKeyHint")} value={key} onChange={(e) => setKey(e.target.value)} error={fe["key"] ?? null} spellCheck={false} />
          <TextField label={t("memory.decisionTitle")} value={title} onChange={(e) => setTitle(e.target.value)} error={fe["title"] ?? null} maxLength={200} />
          <TextAreaField label={t("memory.decisionBody")} value={body} onChange={(e) => setBody(e.target.value)} error={fe["body"] ?? null} rows={6} fieldClassName="field--full" />
          <TextAreaField label={t("memory.evidence")} hint={t("memory.evidenceHint")} value={evidence} onChange={(e) => setEvidence(e.target.value)} rows={3} optionalLabel={t("common.optional")} fieldClassName="field--full" />
          <CheckboxField label={t("memory.requestValidated")} hint={t("memory.validatedHint")} checked={validated} onChange={(e) => setValidated(e.target.checked)} fieldClassName="field--full" />
        </div>
      ) : origin === "prd_version" ? (
        <SelectField label={t("memory.prdVersion")} value={prdVersion} onChange={(e) => setPrdVersion(e.target.value)} error={fe["prd_version_id"] ?? null} hint={versions.length === 0 && !prds.loading ? t("memory.noPrdVersions") : undefined}>
          <option value="">{t("memory.choosePrdVersion")}</option>
          {versions.map((v) => (
            <option key={v.id} value={v.id}>
              {v.label}
            </option>
          ))}
        </SelectField>
      ) : (
        <div className="form-grid">
          <SelectField label={t("memory.workspace")} value={workspace} onChange={(e) => setWorkspace(e.target.value)} error={fe["workspace_id"] ?? null} hint={resources.workspaces.length === 0 ? t("memory.noWorkspaces") : undefined}>
            <option value="">{t("memory.chooseWorkspace")}</option>
            {resources.workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.root_path} ({w.operating_system})
              </option>
            ))}
          </SelectField>
          <TextField label={t("memory.commit")} value={commit} onChange={(e) => setCommit(e.target.value)} error={fe["commit"] ?? null} spellCheck={false} placeholder="a1b2c3d" />
          <TextAreaField label={t("memory.paths")} hint={t("memory.pathsHint")} value={paths} onChange={(e) => setPaths(e.target.value)} error={fe["paths"] ?? null} rows={4} spellCheck={false} fieldClassName="field--full" />
        </div>
      )}
      <div className="form-actions">
        <Button type="submit" variant="primary" loading={busy}>
          {t("memory.ingest")}
        </Button>
      </div>
      {result ? (
        <div className="table-wrap" data-testid="ingest-result">
          <table className="table">
            <caption className="sr-only">{t("memory.ingestResult")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("memory.col.source")}</th>
                <th scope="col">{t("memory.outcome")}</th>
                <th scope="col">{t("memory.detail")}</th>
              </tr>
            </thead>
            <tbody>
              {result.results.map((r) => (
                <tr key={r.source_key} data-outcome={r.outcome}>
                  <td className="mono">{r.source_key}</td>
                  <td>
                    <StatePill tone={r.outcome === "created" ? "success" : r.outcome === "unchanged" ? "info" : "danger"} label={t(`memOutcome.${r.outcome}`)} />
                  </td>
                  <td className="user-content">
                    {r.outcome === "refused" ? `${r.reason ?? ""}${r.detail ? ` — ${r.detail}` : ""}` : r.chunks !== null ? t("memory.chunks", { n: r.chunks }) : r.source ? `v${r.source.version} · ${t(SOURCE_STATE_VIEW[r.source.state].label)}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </form>
  );
}

// ---------------- Stats ----------------

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="stat">
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

function MemoryStatsPanel({ projectId }: { projectId: string }) {
  const { t, locale, formatDateTime } = useI18n();
  const stats = useAsync((s) => getMemoryStats(projectId, s), [projectId]);
  const d = stats.data;
  const n = (v: number) => formatNumber(v, locale);
  return (
    <section className="card" aria-labelledby={`memory-stats-${projectId}`} data-testid="memory-stats">
      <div className="card-header">
        <h2 id={`memory-stats-${projectId}`} className="card-title">
          {t("memory.statsTitle")}
        </h2>
      </div>
      <div className="card-body stack">
        {stats.loading ? (
          <LoadingBlock label={t("common.loading")} />
        ) : stats.error ? (
          <ErrorBanner error={stats.error} onRetry={stats.reload} />
        ) : d ? (
          <>
            <h3 className="subheading">{t("memory.statsSources")}</h3>
            <div className="stat-grid">
              <Stat label={t("memory.stat.current")} value={n(d.sources.current)} />
              <Stat label={t("memory.stat.historical")} value={n(d.sources.historical_versions)} />
              <Stat label={t("memory.stat.withoutEvidence")} value={n(d.sources.without_evidence)} />
              <Stat label={t("memory.stat.obsolete")} value={n(d.sources.obsolete)} />
              <Stat label={t("memory.stat.rejected")} value={n(d.sources.rejected)} />
              <Stat label={t("memory.stat.tombstoned")} value={n(d.sources.tombstoned_keys)} />
            </div>
            <p className="subtle">
              {t("memory.stat.oldest", { when: formatDateTime(d.sources.oldest_current_at) })} · {t("memory.stat.newest", { when: formatDateTime(d.sources.newest_at) })}
            </p>
            <h3 className="subheading">{t("memory.statsIndex")}</h3>
            <div className="stat-grid">
              <Stat label={t("memory.stat.chunks")} value={n(d.index.chunks)} />
              <Stat label={t("memory.stat.bytes")} value={n(d.index.content_bytes)} />
              <Stat label={t("memory.stat.duplicates")} value={n(d.index.duplicate_source_groups)} />
              <Stat label={t("memory.stat.conflicts")} value={n(d.index.conflicting_symbols)} />
            </div>
            {d.index.active_index_version_id === null ? <p className="field-hint">{t("memory.noActiveIndex")}</p> : null}
            {d.index.conflicting_symbol_samples.length > 0 ? <p className="subtle mono">{d.index.conflicting_symbol_samples.join(", ")}</p> : null}
            <h3 className="subheading">{t("memory.statsRetrievals")}</h3>
            <div className="stat-grid">
              <Stat label={t("memory.stat.retrievals")} value={n(d.retrievals.total)} />
              <Stat label={t("memory.stat.empty")} value={n(d.retrievals.without_results)} />
              <Stat label={t("memory.stat.latencyMean")} value={d.retrievals.latency_ms_mean === null ? t("memory.unknown") : formatMs(d.retrievals.latency_ms_mean)} />
              <Stat label={t("memory.stat.latencyP95")} value={d.retrievals.latency_ms_p95 === null ? t("memory.unknown") : formatMs(d.retrievals.latency_ms_p95)} />
              <Stat label={t("memory.stat.tokens")} value={n(d.retrievals.inserted_tokens_estimate)} />
              <Stat label={t("memory.stat.cost")} value={`${d.retrieval_cost.total} ${d.retrieval_cost.currency}`} />
            </div>
            <p className="field-hint">{d.retrieval_cost.note}</p>
          </>
        ) : null}
      </div>
    </section>
  );
}
